import { Html5Qrcode } from 'html5-qrcode';
import { useEffect, useRef, useState } from 'react';
import { Camera, AlertCircle } from 'lucide-react';

interface QRScannerProps {
  onScan: (decodedText: string) => void;
  onError?: (error: string) => void;
  className?: string;
}

export const QRScanner: React.FC<QRScannerProps> = ({
  onScan,
  onError,
  className = ''
}) => {
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string>('');
  const [cameras, setCameras] = useState<any[]>([]);
  const [selectedCamera, setSelectedCamera] = useState<string>('');
  const scannerRef = useRef<Html5Qrcode | null>(null);

  useEffect(() => {
    initScanner();
    return () => {
      stopScanner();
    };
    // eslint-disable-next-line
  }, []);

  const initScanner = async () => {
    try {
      // Get available cameras
      const devices = await Html5Qrcode.getCameras();

      if (devices && devices.length > 0) {
        setCameras(devices);

        // Prefer back camera on mobile
        const backCamera = devices.find(device =>
          device.label.toLowerCase().includes('back') ||
          device.label.toLowerCase().includes('rear')
        );

        const cameraId = backCamera ? backCamera.id : devices[0].id;
        setSelectedCamera(cameraId);
        startScanner(cameraId);
      } else {
        setError('No cameras found. Please ensure camera permissions are granted.');
        if (onError) onError('No cameras found');
      }
    } catch (err: any) {
      setError('Failed to access camera. Please check permissions.');
      if (onError) onError(err.message);
    }
  };

  const startScanner = async (cameraId: string) => {
    try {
      const scanner = new Html5Qrcode("qr-reader");
      scannerRef.current = scanner;

      await scanner.start(
        cameraId,
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0
        },
        (decodedText) => {
          // Success callback
          stopScanner();
          onScan(decodedText);
        },
        () => {
          // Error callback - too verbose, ignore most errors
          // Only log if needed for debugging
        }
      );

      setIsScanning(true);
      setError('');
    } catch (err: any) {
      setError('Failed to start scanner: ' + err.message);
      if (onError) onError(err.message);
    }
  };

  const stopScanner = async () => {
    if (scannerRef.current && isScanning) {
      try {
        await scannerRef.current.stop();
        scannerRef.current.clear();
        setIsScanning(false);
      } catch (err) {
        console.error('Error stopping scanner:', err);
      }
    }
  };

  const switchCamera = async (cameraId: string) => {
    await stopScanner();
    setSelectedCamera(cameraId);
    await startScanner(cameraId);
  };

  return (
    <div className={`qr-scanner-container ${className}`}>
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex items-center gap-2">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Camera Selector */}
      {cameras.length > 1 && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            Select Camera
          </label>
          <select
            value={selectedCamera}
            onChange={(e) => switchCamera(e.target.value)}
            className="input w-full"
          >
            {cameras.map((camera) => (
              <option key={camera.id} value={camera.id}>
                {camera.label || `Camera ${camera.id}`}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Scanner Display */}
      <div className="relative">
        <div
          id="qr-reader"
          className="w-full rounded-lg overflow-hidden border-2 border-primary-500"
        ></div>

        {!isScanning && !error && (
          <div className="absolute inset-0 flex items-center justify-center bg-secondary-100 rounded-lg">
            <div className="text-center">
              <Camera size={48} className="mx-auto mb-2 text-secondary-400" />
              <p className="text-secondary-600">Initializing camera...</p>
            </div>
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="mt-4 text-center text-sm text-secondary-600">
        <p>Point your camera at the event QR code</p>
        <p className="text-xs mt-1">The code will be scanned automatically</p>
      </div>
    </div>
  );
};

export default QRScanner;
